import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import {
  Bed,
  CalendarDays,
  CalendarCheck,
  CalendarClock,
  DoorOpen,
  Wallet,
  Calculator,
  Receipt,
  Languages,
  Moon,
  Wrench,
  UserCheck,
  Package,
  Users,
  CreditCard,
  ShieldCheck,
  Check,
  ArrowRight,
  Star,
  Menu,
  X,
  Globe,
  Sparkles,
  Building2,
  Phone,
  ChevronRight,
  BarChart3,
  Clock,
  Zap,
  Play,
  MousePointerClick,
} from 'lucide-react';

// --- Zaff tree mark (ዛፍ = tree in Amharic) ---
function ZaffTree({ className = 'w-5 h-5' }) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {/* canopy */}
      <g fill="currentColor">
        <circle cx="32" cy="20" r="13.5" />
        <circle cx="19" cy="28.5" r="9.5" />
        <circle cx="45" cy="28.5" r="9.5" />
      </g>
      {/* trunk */}
      <path d="M32 29v27" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round" />
      {/* roots */}
      <path
        d="M32 47c-4.2 0-7.5 2.7-9.5 6.5M32 47c4.2 0 7.5 2.7 9.5 6.5"
        stroke="currentColor"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// --- Domain-aware link helper ---
const getAppUrl = (path) => {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') { return path; }
  if (h.includes('zaffsoftware.com')) { return `https://hotels.zaffsoftware.com${path}`; }
  // hotels.ymcnstay.com was retired in Sep 2026; zaffsoftware.com replaced it.
  return path;
};

// --- Animation Variants ---
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.12 } },
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const slideInLeft = {
  initial: { opacity: 0, x: -60 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const slideInRight = {
  initial: { opacity: 0, x: 60 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

// --- Data ---
const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Why Us', href: '#why-us' },
  { label: 'Contact', href: '#contact' },
];

// Short, scannable list of what the platform actually does - one small box each.
const services = [
  { icon: CalendarCheck, title: 'Booking', note: 'Reservations & walk-ins' },
  { icon: DoorOpen, title: 'Check-in / Check-out', note: 'Fast front desk' },
  { icon: Bed, title: 'Rooms & Rates', note: 'Status & availability' },
  { icon: Users, title: 'Guest Profiles', note: 'History & preferences' },
  { icon: Sparkles, title: 'Housekeeping', note: 'Room cleaning tasks' },
  { icon: Wrench, title: 'Maintenance', note: 'Repairs & work orders' },
  { icon: Wallet, title: 'Employee Payroll', note: 'Salary, pension, tax' },
  { icon: Calculator, title: 'Accounting', note: 'Ledger & expenses' },
  { icon: CalendarClock, title: 'Employee Scheduling', note: 'Shifts & rosters' },
  { icon: UserCheck, title: 'Attendance', note: 'Clock in & leave' },
  { icon: CreditCard, title: 'Payments', note: 'Cash, bank, mobile money' },
  { icon: Receipt, title: 'VAT & TOT Tax', note: 'ERCA-ready reports' },
  { icon: Package, title: 'Inventory', note: 'Stock & supplies' },
  { icon: BarChart3, title: 'Reports & Analytics', note: 'Occupancy, ADR, RevPAR' },
  { icon: Moon, title: 'Night Audit', note: 'Automated day close' },
  { icon: CalendarDays, title: 'Ethiopian Calendar', note: 'EC + Gregorian' },
  { icon: Languages, title: 'Amharic & English', note: 'One-click toggle' },
  { icon: ShieldCheck, title: 'Roles & Security', note: 'Per-staff permissions' },
];

const pricingTiers = [
  {
    id: 'trial',
    name: 'Free Trial',
    badge: 'No credit card',
    beds: 'Up to 10 rooms',
    pricePerRoom: 0,
    description: 'Experience the full platform free for 14 days.',
    features: [
      'All features unlocked',
      'Up to 10 rooms',
      'Full support during trial',
      'Easy upgrade anytime',
    ],
    cta: 'Start Free Trial',
    popular: false,
    // Solid card background + white text for readability
    cardClass: 'bg-gradient-to-br from-slate-700 to-slate-900 border border-white/15',
    badgeClass: 'bg-white/20 text-white',
    ctaClass: 'bg-white/20 text-white hover:bg-white/30',
  },
  {
    id: 'basic',
    name: 'Basic',
    badge: 'Best to start',
    beds: 'Any hotel size',
    pricePerRoom: 150,
    description: 'Everything you need to run the front desk and back office.',
    features: [
      'Booking & reservations',
      'Check-in / check-out',
      'Rooms, rates & guest profiles',
      'Housekeeping & maintenance',
      'Employee scheduling & attendance',
      'Daily reports',
      'Ethiopian calendar & Amharic',
      'Cash & bank transfer payments',
    ],
    cta: 'Get Started',
    popular: false,
    cardClass: 'bg-gradient-to-br from-emerald-700 to-emerald-950 border border-white/15',
    badgeClass: 'bg-white/20 text-white',
    ctaClass: 'bg-white text-emerald-900 hover:bg-emerald-50',
  },
  {
    id: 'pro',
    name: 'Pro',
    badge: 'Most complete',
    beds: 'Any hotel size',
    pricePerRoom: 200,
    description: 'The full suite, including accounting, payroll and analytics.',
    features: [
      'Everything in Basic',
      'Accounting & general ledger',
      'Employee payroll & pension',
      'VAT / TOT tax reports (ERCA)',
      'Integrated payments',
      'Night audit workflow',
      'Reports & revenue analytics',
      'Inventory & stock',
      'Custom branding & API access',
      'Priority support',
    ],
    cta: 'Get Started',
    popular: true,
    cardClass: 'bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 border-2 border-ethiopian-yellow/60',
    badgeClass: 'bg-ethiopian-yellow/25 text-white',
    ctaClass: 'bg-ethiopian-yellow text-slate-900 hover:bg-amber-300',
  },
];

const testimonials = [
  {
    name: 'Abebe Kebede',
    role: 'Owner, Bole Heights Hotel',
    quote: 'We switched from spreadsheets to Zaff in one afternoon. The Ethiopian calendar and ETB formatting finally feel like home.',
    rating: 5,
    avatar: 'AK',
  },
  {
    name: 'Hiwot Tadesse',
    role: 'Front Desk Manager',
    quote: 'Check-ins are smoother, the night audit is automatic, and our owner can see revenue in real time from his phone.',
    rating: 5,
    avatar: 'HT',
  },
  {
    name: 'Dawit Mekonnen',
    role: 'Accountant, Sunset Lodge',
    quote: 'VAT, TOT, and payroll reports that actually match ERCA requirements. This alone saved us hours every month.',
    rating: 5,
    avatar: 'DM',
  },
];

const heroImages = [
  '/addis-night-flag.webp',
  '/addis-night-park-2.jpg',
  '/addis-skyline.jpg',
  '/addis-panorama.jpeg',
];

function formatPrice(amount) {
  if (amount === 0) { return 'Free'; }
  return `${amount.toLocaleString('en-ET')} ETB`;
}

// --- Animated Counter Component ---
function AnimatedCounter({ value, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= numericValue) {
          setCount(numericValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, numericValue]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
}

// --- Navigation ---
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-slate-900/90 backdrop-blur-2xl shadow-lg shadow-black/20 border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-2.5 group">
            <motion.div
              whileHover={{ rotate: 5, scale: 1.05 }}
              className="w-14 h-14 rounded-xl bg-gradient-to-br from-ethiopian-green via-emerald-600 to-emerald-500 flex items-center justify-center shadow-lg shadow-ethiopian-green/30"
            >
              <ZaffTree className="w-9 h-9 text-white" />
            </motion.div>
            <span className={'font-bold text-4xl tracking-tight transition-colors duration-300 text-white'}>
              ዛፍ
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:bg-white/10 ${
                  scrolled ? 'text-slate-300 hover:text-white hover:bg-white/10' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={getAppUrl('/login')}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                scrolled
                  ? 'text-slate-300 hover:text-white hover:bg-white/10'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              Sign In
            </a>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <a
                href={getAppUrl('/register')}
                className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-ethiopian-green to-emerald-600 text-white shadow-lg shadow-ethiopian-green/25 hover:shadow-ethiopian-green/40 transition-all"
              >
                Get Started Free
              </a>
            </motion.div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-white hover:bg-white/10' : 'text-white hover:bg-white/10'}`}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden bg-slate-900/95 backdrop-blur-xl border-b border-white/10 shadow-xl overflow-hidden"
          >
            <div className="px-4 py-5 space-y-1">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="block px-4 py-3 text-slate-300 font-medium rounded-xl hover:bg-white/10 transition-colors"
                >
                  {link.label}
                </motion.a>
              ))}
              <div className="pt-4 border-t border-white/10 flex flex-col gap-2">
                <a href={getAppUrl('/login')} onClick={() => setMobileOpen(false)} className="px-4 py-3 text-center text-slate-300 font-medium rounded-xl hover:bg-white/10">
                  Sign In
                </a>
                <a href={getAppUrl('/register')} onClick={() => setMobileOpen(false)} className="px-4 py-3 text-center font-semibold rounded-xl bg-ethiopian-green text-white hover:bg-emerald-700 transition-colors">
                  Get Started Free
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

// --- Hero with Image Carousel ---
function Hero() {
  const [currentImage, setCurrentImage] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax Background Images */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImage}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <motion.div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-110"
            style={{ backgroundImage: `url(${heroImages[currentImage]})`, y }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 via-transparent to-slate-900/30" />

      {/* Animated Grain/Noise Texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")' }} />

      {/* Hero Content */}
      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-44 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-sm font-medium mb-8 shadow-xl shadow-black/10"
        >
          <motion.div animate={{ rotate: [0, 15, -15, 0] }} transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}>
            <Sparkles className="w-4 h-4 text-ethiopian-yellow" />
          </motion.div>
          <span>Ethiopia's #1 Hotel Management Platform</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-[5.5rem] font-extrabold text-white tracking-tight leading-[1.05] mb-8"
        >
          Run Your Hotel Like a
          <motion.span
            initial={{ backgroundPosition: '200% 0' }}
            animate={{ backgroundPosition: '0% 0' }}
            transition={{ duration: 2, delay: 0.8, ease: 'easeOut' }}
            className="block mt-2 bg-gradient-to-r from-ethiopian-yellow via-amber-300 to-white bg-[length:200%_auto] bg-clip-text text-transparent"
          >
            Modern Powerhouse
          </motion.span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mx-auto text-lg sm:text-xl text-white/80 mb-12 leading-relaxed"
        >
          The all-in-one property management system designed for small and medium Ethiopian hotels.
          Reservations, accounting, payroll, tax reports, and staff portals - in ETB, Amharic, and the Ethiopian calendar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14"
        >
          <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
            <a
              href={getAppUrl('/register')}
              className="group px-8 py-4 text-base font-semibold rounded-2xl bg-gradient-to-r from-ethiopian-yellow to-amber-500 text-slate-900 shadow-2xl shadow-ethiopian-yellow/30 hover:shadow-ethiopian-yellow/50 transition-all flex items-center gap-2"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
            </a>
          </motion.div>
          <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
            <a
              href={getAppUrl('/login')}
              className="px-8 py-4 text-base font-semibold rounded-2xl bg-white/10 backdrop-blur-md border border-white/25 text-white hover:bg-white/20 hover:border-white/40 transition-all flex items-center gap-2"
            >
              <Play className="w-4 h-4" />
              See Demo
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-white/70 text-sm font-medium"
        >
          {['14-day free trial', 'No credit card required', 'Cancel anytime'].map((item, i) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + i * 0.1 }}
              className="flex items-center gap-2"
            >
              <div className="w-5 h-5 rounded-full bg-ethiopian-green/20 flex items-center justify-center">
                <Check className="w-3 h-3 text-ethiopian-green" />
              </div>
              <span>{item}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Image Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentImage(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === currentImage ? 'w-8 bg-white' : 'w-3 bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-slate-900 to-transparent" />

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5"
        >
          <motion.div className="w-1.5 h-3 rounded-full bg-white/60" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// --- Trust Strip with Animated Counters ---
function TrustStrip() {
  const stats = [
    { value: '50', suffix: '+', label: 'Hotels Onboarded', icon: Building2 },
    { value: '12000', suffix: '+', label: 'Bookings Managed', icon: CalendarDays },
    { value: '99', suffix: '.9%', label: 'Uptime SLA', icon: Zap },
    { value: '24', suffix: 'h', label: 'Support Response', icon: Clock },
  ];

  return (
    <section className="relative py-16 overflow-hidden">
      {/* Image Background */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/addis-panorama.jpeg)' }} />
      <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              variants={fadeInUp}
              className="text-center group"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-ethiopian-green/20 text-ethiopian-green mb-3 group-hover:scale-110 transition-transform duration-300">
                <stat.icon className="w-6 h-6" />
              </div>
              <div className="text-3xl sm:text-4xl font-extrabold text-white">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm text-slate-400 font-medium mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// --- Services: many small boxes, one per capability ---
function Features() {
  return (
    <section id="features" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Image Background */}
      <div className="absolute inset-0 bg-cover bg-center bg-fixed" style={{ backgroundImage: 'url(/addis-skyline.jpg)' }} />
      <div className="absolute inset-0 bg-slate-900/90" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-14">
          <motion.span
            {...scaleIn}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ethiopian-yellow/10 text-ethiopian-yellow text-sm font-semibold mb-6 border border-ethiopian-yellow/20"
          >
            <Zap className="w-4 h-4" />
            Everything You Need
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
            One Platform for Your
            <span className="bg-gradient-to-r from-ethiopian-yellow to-amber-300 bg-clip-text text-transparent"> Entire Operation</span>
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            Front desk to back office, all in one place.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: '-80px' }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4"
        >
          {services.map((service) => (
            <motion.div
              key={service.title}
              variants={fadeInUp}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
              className="group p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/[0.09] hover:border-ethiopian-green/40 transition-all duration-300 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-ethiopian-green/15 text-ethiopian-green flex items-center justify-center group-hover:bg-ethiopian-green group-hover:text-white transition-colors duration-300">
                <service.icon className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-semibold text-white leading-snug">{service.title}</h3>
              <p className="text-xs text-slate-400 mt-1 leading-snug">{service.note}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// --- Dashboard Preview with Parallax ---
function DashboardPreview() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section id="why-us" ref={sectionRef} className="py-24 lg:py-32 relative overflow-hidden">
      {/* Parallax Background */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/addis-night-flag.webp)' }} />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 via-slate-900/85 to-primary-900/80" />
      <div className="absolute inset-0 backdrop-blur-[2px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div {...slideInLeft}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-semibold mb-6 border border-white/10 backdrop-blur-sm">
              <Globe className="w-4 h-4 text-ethiopian-yellow" />
              Designed for Ethiopia
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
              Local Compliance.
              <span className="block text-ethiopian-yellow">Global Quality.</span>
            </h2>
            <p className="text-lg text-white/70 mb-10 leading-relaxed">
              Zaff combines international-grade UX with the local details that matter: ETB currency, Amharic UI,
              Ethiopian calendar, and tax reports formatted for ERCA.
            </p>

            <div className="space-y-4">
              {[
                'Ethiopian Calendar (EC) + Gregorian dual calendar',
                'Amharic language toggle across the entire platform',
                'VAT 15%, TOT 2%, withholding, and pension auto-calculation',
                'ETB/ብር formatting on every invoice, receipt, and report',
                'Bank export files for CBE, Dashen, Awash, Abyssinia, Wegagen',
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  className="flex items-start gap-3 group"
                >
                  <div className="w-6 h-6 rounded-lg bg-ethiopian-green/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-ethiopian-green/30 transition-colors">
                    <Check className="w-4 h-4 text-ethiopian-green" />
                  </div>
                  <span className="text-white/90 font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div {...slideInRight} className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-6 bg-gradient-to-r from-ethiopian-green/15 via-ethiopian-yellow/10 to-ethiopian-red/15 rounded-[2rem] blur-3xl animate-pulse" />

            {/* Room Management Screenshot */}
            <div className="relative rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
              <div className="bg-slate-800 border-b border-white/10 px-6 py-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-ethiopian-yellow" />
                <div className="w-3 h-3 rounded-full bg-ethiopian-green" />
                <span className="ml-4 text-sm text-slate-400 font-medium">Room Management</span>
              </div>
              <img
                src="/room-management-shot.png"
                alt="Room Management screen"
                className="w-full block"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// --- Dashboard Screenshot Section ---
function DashboardScreenshot() {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-slate-950" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ethiopian-green/10 text-ethiopian-green text-sm font-semibold mb-6 border border-ethiopian-green/20">
            <BarChart3 className="w-4 h-4" />
            Real-time Insights
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
            Your Hotel at a
            <span className="bg-gradient-to-r from-ethiopian-green to-emerald-400 bg-clip-text text-transparent"> Glance</span>
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            Revenue, occupancy, and check-ins  all on one dashboard, updated in real time.
          </p>
        </motion.div>

        <div className="flex flex-col gap-10">
          <motion.div {...scaleIn} className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-ethiopian-green/10 via-primary-500/10 to-ethiopian-yellow/10 rounded-[2rem] blur-3xl" />
            <div className="relative rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
              <div className="bg-slate-800 border-b border-white/10 px-6 py-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-ethiopian-yellow" />
                <div className="w-3 h-3 rounded-full bg-ethiopian-green" />
                <span className="ml-4 text-sm text-slate-400 font-medium">Room Management</span>
              </div>
              <img
                src="/room-management-shot.png"
                alt="Room Management screen"
                className="w-full block"
              />
            </div>
          </motion.div>

          <motion.div {...scaleIn} className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/10 via-ethiopian-yellow/10 to-ethiopian-green/10 rounded-[2rem] blur-3xl" />
            <div className="relative rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
              <div className="bg-slate-800 border-b border-white/10 px-6 py-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-ethiopian-yellow" />
                <div className="w-3 h-3 rounded-full bg-ethiopian-green" />
                <span className="ml-4 text-sm text-slate-400 font-medium">Hotel Admin Dashboard</span>
              </div>
              <img
                src="/dashboard-shot.png"
                alt="Hotel Admin Dashboard"
                className="w-full block"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// --- City Showcase Section ---
function CityShowcase() {
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Subtle dark bg */}
      <div className="absolute inset-0 bg-slate-900" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Powering Hotels Across Ethiopia
          </h2>
          <p className="text-slate-400">From Addis Ababa to Hawassa, Bahir Dar to Dire Dawa</p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { src: '/addis-skyline.jpg', label: 'Addis Ababa' },
            { src: '/addis-night-flag.webp', label: 'Addis by Night' },
            { src: '/addis-night-park-2.jpg', label: 'City Lights' },
            { src: '/addis-panorama.jpeg', label: 'Panorama' },
          ].map((img, i) => (
            <motion.div
              key={img.label}
              variants={fadeInUp}
              whileHover={{ scale: 1.03, y: -5 }}
              className={`relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer ${
                i === 0 ? 'md:row-span-2 aspect-[3/4] md:aspect-auto' : 'aspect-[4/3]'
              }`}
            >
              <img src={img.src} alt={img.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 text-white font-semibold text-sm">{img.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// --- Pricing ---
function Pricing() {
  return (
    <section id="pricing" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Image Background */}
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/addis-night-park-2.jpg)' }} />
      <div className="absolute inset-0 bg-slate-900/88" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ethiopian-red/10 text-red-400 text-sm font-semibold mb-6 border border-ethiopian-red/20">
            <CreditCard className="w-4 h-4" />
            Simple Per-Room Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6">
            Choose the Plan That Fits Your Hotel
          </h2>
          <p className="text-lg text-slate-300">
            No hidden fees. Pay in ETB, per room, per month. Cancel anytime.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: '-80px' }}
          className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto"
        >
          {pricingTiers.map((tier) => (
            <motion.div
              key={tier.id}
              variants={fadeInUp}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className={`relative rounded-3xl p-7 shadow-2xl shadow-black/40 transition-all duration-500 ${tier.cardClass} ${
                tier.popular ? 'md:scale-[1.03]' : ''
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gradient-to-r from-ethiopian-yellow to-amber-400 text-slate-900 text-xs font-bold shadow-lg">
                  Most Popular
                </div>
              )}

              <div className="mb-5">
                <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold ${tier.badgeClass}`}>
                  {tier.badge}
                </span>
              </div>

              <h3 className="text-xl font-bold text-white mb-1">{tier.name}</h3>
              <p className="text-sm text-white/70 mb-5">{tier.beds}</p>

              <div className="mb-5 flex flex-wrap items-baseline gap-x-1.5">
                <span className="text-4xl font-extrabold text-white">
                  {formatPrice(tier.pricePerRoom)}
                </span>
                {tier.pricePerRoom > 0
                  ? (
                    <span className="text-white/75 text-sm font-medium">/ room / month</span>
                  )
                  : (
                    <span className="text-white/75 text-sm font-medium">for 14 days</span>
                  )}
              </div>

              <p className="text-sm text-white/80 mb-6 min-h-[40px]">{tier.description}</p>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <a
                  href={getAppUrl('/register')}
                  className={`block w-full text-center py-3 rounded-xl font-semibold shadow-lg transition-all mb-6 ${tier.ctaClass}`}
                >
                  {tier.cta}
                </a>
              </motion.div>

              <ul className="space-y-3">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm text-white">
                    <Check className="w-4 h-4 text-ethiopian-yellow flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// --- Testimonials ---
function Testimonials() {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Image Background */}
      <div className="absolute inset-0 bg-cover bg-center bg-fixed" style={{ backgroundImage: 'url(/addis-panorama.jpeg)' }} />
      <div className="absolute inset-0 bg-slate-900/90" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ethiopian-green/10 text-ethiopian-green text-sm font-semibold mb-6 border border-ethiopian-green/20">
            <Star className="w-4 h-4 fill-ethiopian-yellow text-ethiopian-yellow" />
            Loved by Hoteliers
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Trusted Across Ethiopia
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: '-80px' }}
          className="grid md:grid-cols-3 gap-6"
        >
          {testimonials.map((t) => (
            <motion.div
              key={t.name}
              variants={fadeInUp}
              whileHover={{ y: -5 }}
              className="p-8 rounded-3xl bg-white/5 border border-white/10 shadow-sm hover:shadow-xl hover:bg-white/[0.08] transition-all duration-500"
            >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-ethiopian-yellow text-ethiopian-yellow" />
                ))}
              </div>
              <p className="text-slate-300 mb-6 leading-relaxed text-[15px]">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-700 to-primary-900 flex items-center justify-center text-white text-sm font-bold">
                  {t.avatar}
                </div>
                <div>
                  <div className="font-bold text-white text-sm">{t.name}</div>
                  <div className="text-xs text-slate-400">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// --- CTA Section ---
function CtaSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="contact" ref={sectionRef} className="relative py-28 lg:py-36 overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/addis-night-park-2.jpg)' }} />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900/95 via-slate-900/85 to-primary-900/80" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div {...fadeInUp}>
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
          >
            <MousePointerClick className="w-8 h-8 text-white" />
          </motion.div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
            Ready to Transform
            <span className="block text-ethiopian-red">Your Hotel?</span>
          </h2>
          <p className="text-lg sm:text-xl text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
            Join Ethiopian hotels already using Zaff to save time, reduce errors, and increase revenue.
            Start your 14-day free trial today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
              <a
                href={getAppUrl('/register')}
                className="group px-10 py-4.5 text-base font-semibold rounded-2xl bg-gradient-to-r from-ethiopian-yellow to-amber-500 text-slate-900 shadow-2xl shadow-ethiopian-yellow/30 hover:shadow-ethiopian-yellow/50 transition-all flex items-center gap-2"
              >
                Start Free Trial
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
              </a>
            </motion.div>
            <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
              <a
                href="tel:+251911234567"
                className="px-10 py-4.5 text-base font-semibold rounded-2xl bg-white/10 backdrop-blur-md border border-white/25 text-white hover:bg-white/20 hover:border-white/40 transition-all flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call Us
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// --- Footer ---
function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 pt-20 pb-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-12 mb-16">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-ethiopian-green via-emerald-600 to-emerald-500 flex items-center justify-center shadow-lg shadow-ethiopian-green/30">
                <ZaffTree className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl text-white">ዛፍ</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mb-6">
              Modern property management software built for Ethiopian hotels. Local compliance, global quality.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Globe className="w-4 h-4" />
              <span>Made in Addis Ababa, Ethiopia</span>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Product</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#features" className="text-slate-400 hover:text-white transition-colors duration-200">Features</a></li>
              <li><a href="#pricing" className="text-slate-400 hover:text-white transition-colors duration-200">Pricing</a></li>
              <li><a href={getAppUrl('/register')} className="text-slate-400 hover:text-white transition-colors duration-200">Get Started</a></li>
              <li><a href={getAppUrl('/login')} className="text-slate-400 hover:text-white transition-colors duration-200">Sign In</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Company</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">About</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">Contact</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">Careers</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Legal</h4>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">Privacy Policy</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors duration-200">Terms of Service</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Zaff Software. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a href="#" className="hover:text-white transition-colors">Twitter</a>
            <a href="#" className="hover:text-white transition-colors">LinkedIn</a>
            <a href="#" className="hover:text-white transition-colors">Telegram</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// --- Main Export ---
export default function LandingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 font-sans antialiased">
      <Navigation />
      <Hero />
      <TrustStrip />
      <Features />
      <DashboardPreview />
      <DashboardScreenshot />
      <CityShowcase />
      <Pricing />
      <Testimonials />
      <CtaSection />
      <Footer />
    </div>
  );
}
